import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, IconButton, CircularProgress,
    Alert, Fade, LinearProgress
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

// ─── Geometry helpers ───────────────────────────────────────────────────────

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function detectReceiptRect(imageData, scaleX, scaleY) {
    const { width, height, data } = imageData;

    // 1. Grayscale conversion
    const totalPixels = width * height;
    const grayData = new Uint8Array(totalPixels);
    for (let i = 0; i < totalPixels; i++) {
        const r = data[i * 4];
        const g = data[i * 4 + 1];
        const b = data[i * 4 + 2];
        grayData[i] = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    }

    // 2. Local Adaptive Thresholding (Block-based min-max average)
    // Works extremely well under uneven lighting and shadows compared to global Otsu.
    const BLOCK_SIZE = 16;
    const numBlocksX = Math.ceil(width / BLOCK_SIZE);
    const numBlocksY = Math.ceil(height / BLOCK_SIZE);
    const blockThresholds = new Uint8Array(numBlocksX * numBlocksY);

    for (let by = 0; by < numBlocksY; by++) {
        const y0 = by * BLOCK_SIZE;
        const y1 = Math.min(height, y0 + BLOCK_SIZE);
        for (let bx = 0; bx < numBlocksX; bx++) {
            const x0 = bx * BLOCK_SIZE;
            const x1 = Math.min(width, x0 + BLOCK_SIZE);
            
            let minVal = 255;
            let maxVal = 0;
            for (let y = y0; y < y1; y++) {
                for (let x = x0; x < x1; x++) {
                    const val = grayData[y * width + x];
                    if (val < minVal) minVal = val;
                    if (val > maxVal) maxVal = val;
                }
            }
            
            // If the block is very low contrast, it's likely solid table or solid receipt.
            if (maxVal - minVal < 20) {
                blockThresholds[by * numBlocksX + bx] = 130;
            } else {
                blockThresholds[by * numBlocksX + bx] = Math.round((minVal + maxVal) / 2);
            }
        }
    }

    const binaryMask = new Uint8Array(totalPixels);
    for (let y = 0; y < height; y++) {
        const by = Math.floor(y / BLOCK_SIZE);
        for (let x = 0; x < width; x++) {
            const bx = Math.floor(x / BLOCK_SIZE);
            const thresh = blockThresholds[by * numBlocksX + bx];
            const idx = y * width + x;
            const val = grayData[idx];
            
            // Get color values to distinguish skin and warm backgrounds from white paper
            const r = data[idx * 4];
            const g = data[idx * 4 + 1];
            
            // Skin tones and warm-colored surfaces have significantly more red than green
            const isSkinOrWarmColor = (r - g > 20);

            // Must be brighter than local average, at least 110 (bright paper), and not skin/warm background
            binaryMask[idx] = (val > thresh && val > 110 && !isSkinOrWarmColor) ? 1 : 0;
        }
    }

    // 3. Find the largest connected component of white pixels (excluding outer 3% border)
    const visited = new Uint8Array(totalPixels);
    const borderX = Math.round(width * 0.03);
    const borderY = Math.round(height * 0.03);
    const minX = borderX;
    const maxX = width - borderX;
    const minY = borderY;
    const maxY = height - borderY;

    let bestComponent = null;
    let maxPixelCount = 0;

    // BFS queue (reuse buffers to avoid allocations)
    const queueX = new Int32Array(totalPixels);
    const queueY = new Int32Array(totalPixels);

    const dx = [0, 0, -1, 1];
    const dy = [-1, 1, 0, 0];

    for (let y = minY; y < maxY; y++) {
        for (let x = minX; x < maxX; x++) {
            const idx = y * width + x;
            if (binaryMask[idx] === 1 && !visited[idx]) {
                // Start BFS to extract this component
                let head = 0;
                let tail = 0;
                
                queueX[tail] = x;
                queueY[tail] = y;
                tail++;
                visited[idx] = 1;

                let cMinX = Infinity;
                let cMaxX = -Infinity;
                let cMinY = Infinity;
                let cMaxY = -Infinity;
                let pixelCount = 0;

                while (head < tail) {
                    const cx = queueX[head];
                    const cy = queueY[head];
                    head++;
                    pixelCount++;

                    if (cx < cMinX) cMinX = cx;
                    if (cx > cMaxX) cMaxX = cx;
                    if (cy < cMinY) cMinY = cy;
                    if (cy > cMaxY) cMaxY = cy;

                    for (let i = 0; i < 4; i++) {
                        const nx = cx + dx[i];
                        const ny = cy + dy[i];
                        if (nx >= minX && nx < maxX && ny >= minY && ny < maxY) {
                            const nIdx = ny * width + nx;
                            if (!visited[nIdx] && binaryMask[nIdx] === 1) {
                                visited[nIdx] = 1;
                                queueX[tail] = nx;
                                queueY[tail] = ny;
                                tail++;
                            }
                        }
                    }
                }

                if (pixelCount > maxPixelCount) {
                    maxPixelCount = pixelCount;
                    bestComponent = { minX: cMinX, maxX: cMaxX, minY: cMinY, maxY: cMaxY, pixelCount };
                }
            }
        }
    }

    if (!bestComponent) return null;

    // 4. Validate the component size (must be at least 3% of the image)
    const minPixels = totalPixels * 0.03;
    if (bestComponent.pixelCount < minPixels) return null;

    const { minX: bMinX, maxX: bMaxX, minY: bMinY, maxY: bMaxY } = bestComponent;

    // Validate size of bounding box
    const compW = bMaxX - bMinX;
    const compH = bMaxY - bMinY;

    if (compW < 15 || compH < 15) return null;

    // Validate aspect ratio
    const aspect = compW / compH;
    if (aspect < 0.15 || aspect > 6.0) return null;

    // Return the corners scaled back to original video resolution
    return [
        { x: bMinX * scaleX, y: bMinY * scaleY }, // tl
        { x: bMaxX * scaleX, y: bMinY * scaleY }, // tr
        { x: bMaxX * scaleX, y: bMaxY * scaleY }, // br
        { x: bMinX * scaleX, y: bMaxY * scaleY }  // bl
    ];
}

function perspectiveCrop(videoEl, corners) {
    const [tl, tr, br, bl] = corners;
    const srcW = videoEl.videoWidth;
    const srcH = videoEl.videoHeight;

    const outW = Math.round(Math.max(
        Math.hypot(tr.x - tl.x, tr.y - tl.y),
        Math.hypot(br.x - bl.x, br.y - bl.y)
    ));
    const outH = Math.round(Math.max(
        Math.hypot(bl.x - tl.x, bl.y - tl.y),
        Math.hypot(br.x - tr.x, br.y - tr.y)
    ));
    if (outW < 10 || outH < 10) return null;

    const src = document.createElement('canvas');
    src.width = srcW; src.height = srcH;
    const srcCtx = src.getContext('2d');
    srcCtx.drawImage(videoEl, 0, 0, srcW, srcH);
    const srcImageData = srcCtx.getImageData(0, 0, srcW, srcH);
    const srcData = srcImageData.data;

    const dst = document.createElement('canvas');
    dst.width = outW; dst.height = outH;
    const dstCtx = dst.getContext('2d');
    const dstImageData = dstCtx.createImageData(outW, outH);
    const dstData = dstImageData.data;

    for (let dy = 0; dy < outH; dy++) {
        const tv = dy / outH;
        for (let dx = 0; dx < outW; dx++) {
            const tu = dx / outW;
            const sx = (1 - tv) * ((1 - tu) * tl.x + tu * tr.x) + tv * ((1 - tu) * bl.x + tu * br.x);
            const sy = (1 - tv) * ((1 - tu) * tl.y + tu * tr.y) + tv * ((1 - tu) * bl.y + tu * br.y);
            const ix = Math.round(clamp(sx, 0, srcW - 1));
            const iy = Math.round(clamp(sy, 0, srcH - 1));
            const si = (iy * srcW + ix) * 4;
            const di = (dy * outW + dx) * 4;
            dstData[di] = srcData[si];
            dstData[di + 1] = srcData[si + 1];
            dstData[di + 2] = srcData[si + 2];
            dstData[di + 3] = 255;
        }
    }
    dstCtx.putImageData(dstImageData, 0, 0);
    dstCtx.filter = 'contrast(1.15) brightness(1.05)';
    dstCtx.drawImage(dst, 0, 0);
    dstCtx.filter = 'none';

    return { dataUrl: dst.toDataURL('image/jpeg', 0.92), canvas: dst };
}

const ScannerPhase = {
    CAMERA: 'camera',
    FREEZING: 'freezing',
    PREVIEW: 'preview',
    UPLOADING: 'uploading'
};

// ─── Constants ────────────────────────────────────────────────────────────────

const SCAN_INTERVAL_MS = 200;
const STABLE_NEEDED_MS = 2000;
const DOWNSAMPLE_WIDTH = 160;

// Theme colours (matching app theme.js)
const BORDER_COLOR = '#E2E8F0'; // slate-200
const BG_SUBTLE = '#F8FAFC'; // slate-50
const PRIMARY_BLUE = '#2563EB';
const SUCCESS_GREEN = '#10B981';
const TEXT_SECONDARY = '#64748B';

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ReceiptCameraDialog({ open, onClose, onParsed, api, documentType, ledgerId }) {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const overlayRef = useRef(null);
    const scanCanvas = useRef(null);
    const streamRef = useRef(null);
    const scanTimerRef = useRef(null);
    const stableRef = useRef(null);
    const freezeTimerRef = useRef(null);
    const cameraStartTimeRef = useRef(0);

    const [phase, setPhase] = useState(ScannerPhase.CAMERA);
    const [stableProgress, setStableProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewCanvas, setPreviewCanvas] = useState(null);
    const [cameraError, setCameraError] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [detectedCorners, setDetectedCorners] = useState(null);
    const [isMagnifiedOpen, setIsMagnifiedOpen] = useState(false);

    // ── Overlay drawing ───────────────────────────────────────────────────────
    const drawOverlay = useCallback((corners, progress) => {
        const canvas = overlayRef.current;
        const video = videoRef.current;
        if (!canvas || !video) return;
        const W = canvas.width, H = canvas.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, W, H);
        if (!corners) return;

        const scaleX = W / (video.videoWidth || W);
        const scaleY = H / (video.videoHeight || H);
        const pts = corners.map(p => ({ x: p.x * scaleX, y: p.y * scaleY }));

        // Use primary blue while acquiring, success green when nearly captured
        const color = progress > 60 ? SUCCESS_GREEN : PRIMARY_BLUE;
        const alpha = 0.7 + 0.3 * (progress / 100);

        ctx.save();
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;

        // Quadrilateral
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.strokeStyle = color;
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // Corner accent marks
        const MARK = 14;
        [
            [pts[0], [1, 0], [0, 1]],
            [pts[1], [-1, 0], [0, 1]],
            [pts[2], [-1, 0], [0, -1]],
            [pts[3], [1, 0], [0, -1]],
        ].forEach(([p, dx, dy]) => {
            ctx.beginPath();
            ctx.moveTo(p.x + dx[0] * MARK, p.y + dy[0] * MARK);
            ctx.lineTo(p.x, p.y);
            ctx.lineTo(p.x + dx[1] * MARK, p.y + dy[1] * MARK);
            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.stroke();
        });

        // Subtle fill
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.06;
        ctx.fill();

        ctx.restore();
    }, []);

    // ── Scan loop ─────────────────────────────────────────────────────────────
    const runScan = useCallback(() => {
        // Give the camera 2.5 seconds to autofocus and adjust brightness before starting scans
        if (Date.now() - cameraStartTimeRef.current < 2500) {
            stableRef.current = null;
            setStableProgress(0);
            drawOverlay(null, 0);
            return;
        }

        const video = videoRef.current;
        const canvas = scanCanvas.current;
        if (!video || !canvas || video.readyState < 2) return;

        const vW = video.videoWidth, vH = video.videoHeight;
        if (!vW || !vH) return;

        const overlay = overlayRef.current;
        if (overlay && (overlay.width !== vW || overlay.height !== vH)) {
            overlay.width = vW; overlay.height = vH;
        }

        const dsW = DOWNSAMPLE_WIDTH;
        const dsH = Math.round(vH * (dsW / vW));
        canvas.width = dsW; canvas.height = dsH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, dsW, dsH);
        const imageData = ctx.getImageData(0, 0, dsW, dsH);

        const corners = detectReceiptRect(imageData, vW / dsW, vH / dsH);
        setDetectedCorners(corners);

        const now = Date.now();
        if (corners) {
            if (!stableRef.current) stableRef.current = now;
            const elapsed = now - stableRef.current;
            const progress = Math.min(100, (elapsed / STABLE_NEEDED_MS) * 100);
            setStableProgress(progress);
            drawOverlay(corners, progress);

            if (elapsed >= STABLE_NEEDED_MS) {
                stableRef.current = null;
                clearInterval(scanTimerRef.current);
                const result = perspectiveCrop(video, corners);
                if (result) {
                    setPreviewUrl(result.dataUrl);
                    setPreviewCanvas(result.canvas);

                    // Pause the video feed to freeze the screen
                    try {
                        video.pause();
                    } catch (e) {
                        console.warn("Could not pause video", e);
                    }

                    setPhase(ScannerPhase.FREEZING);
                    drawOverlay(corners, 100);

                    // Stay in freezing phase for 1 second before going to preview
                    freezeTimerRef.current = setTimeout(() => {
                        setPhase(ScannerPhase.PREVIEW);
                    }, 1000);
                }
            }
        } else {
            stableRef.current = null;
            setStableProgress(0);
            drawOverlay(null, 0);
        }
    }, [drawOverlay]);

    // ── Camera lifecycle ──────────────────────────────────────────────────────
    const startCamera = useCallback(async () => {
        setCameraError('');
        const isCancelled = { val: false };
        const cleanup = () => { isCancelled.val = true; };

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }
            });

            if (isCancelled.val) {
                stream.getTracks().forEach(t => t.stop());
                return;
            }

            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                // Use a promise-safe play()
                try {
                    await videoRef.current.play();
                } catch (e) {
                    console.warn("Autoplay blocked or video element removed", e);
                }
            }

            // Set the startup time when camera successfully started
            cameraStartTimeRef.current = Date.now();

            // Clear any existing timer just in case
            if (scanTimerRef.current) clearInterval(scanTimerRef.current);
            scanTimerRef.current = setInterval(runScan, SCAN_INTERVAL_MS);
        } catch (err) {
            if (isCancelled.val) return;
            console.error('Camera error:', err);
            if (err.name === 'NotAllowedError') setCameraError('Camera permission denied.');
            else if (err.name === 'NotFoundError') setCameraError('No camera found.');
            else setCameraError('Could not access camera: ' + err.message);
        }
        return cleanup;
    }, [runScan]);

    const stopCamera = useCallback(() => {
        if (scanTimerRef.current) {
            clearInterval(scanTimerRef.current);
            scanTimerRef.current = null;
        }
        if (freezeTimerRef.current) {
            clearTimeout(freezeTimerRef.current);
            freezeTimerRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        stableRef.current = null;
    }, []);

    useEffect(() => {
        let cameraCleanup = null;
        if (open) {
            setPhase(ScannerPhase.CAMERA); setPreviewUrl(null); setPreviewCanvas(null);
            setStableProgress(0); setDetectedCorners(null); setUploadError('');
            startCamera().then(cleanup => { cameraCleanup = cleanup; });
        } else {
            stopCamera();
        }
        return () => {
            if (cameraCleanup) cameraCleanup();
            stopCamera();
        };
    }, [open, startCamera, stopCamera]);

    const handleRetake = () => {
        if (freezeTimerRef.current) {
            clearTimeout(freezeTimerRef.current);
            freezeTimerRef.current = null;
        }
        setPhase(ScannerPhase.CAMERA); setPreviewUrl(null); setPreviewCanvas(null);
        setStableProgress(0); setDetectedCorners(null); setUploadError('');
        setIsMagnifiedOpen(false);
        stableRef.current = null;
        startCamera();
    };

    const handleUsePhoto = async () => {
        if (!previewCanvas) return;
        setPhase(ScannerPhase.UPLOADING); setUploadError('');
        try {
            const blob = await new Promise(resolve => previewCanvas.toBlob(resolve, 'image/jpeg', 0.92));
            const formData = new FormData();
            formData.append('file', blob, 'receipt.jpg');
            const params = new URLSearchParams();
            if (documentType) {
                params.append('document_type', documentType);
            }
            if (ledgerId) {
                params.append('ledger_id', ledgerId);
            }
            const res = await api.post(`/source-documents/parse?${params.toString()}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            window.dispatchEvent(new Event('quota-updated'));
            onParsed(res.data, blob);

            onClose();
        } catch (err) {
            console.error('Upload error:', err);
            const msg = err.response?.data?.detail || 'Failed to read receipt. Please retake or try again.';
            setUploadError(msg);
            setPhase(ScannerPhase.PREVIEW);
        }

    };

    const handleClose = () => {
        stopCamera();
        setIsMagnifiedOpen(false);
        onClose();
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <>
            <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    overflow: 'hidden',
                    m: 2,
                    maxHeight: '90vh',
                }
            }}
        >
            {/* ── Header ── */}
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: `1px solid ${BORDER_COLOR}`,
                py: 1.5, px: 2.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CameraAltIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {phase === ScannerPhase.PREVIEW || phase === ScannerPhase.UPLOADING ? t('Review Receipt') : t('Scan Receipt')}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* ── Camera Error ── */}
                {cameraError && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">{cameraError}</Alert>
                    </Box>
                )}

                {/* ── Camera Phase ── */}
                {(phase === ScannerPhase.CAMERA || phase === ScannerPhase.FREEZING) && !cameraError && (
                    <>
                        {/* Viewfinder — black background makes the feed stand out */}
                        <Box sx={{ position: 'relative', width: '100%', bgcolor: '#000', flexShrink: 0 }}>
                            <video
                                ref={videoRef}
                                autoPlay playsInline muted
                                style={{ width: '100%', display: 'block', maxHeight: '55vh', objectFit: 'cover' }}
                            />
                            <canvas
                                ref={overlayRef}
                                style={{
                                    position: 'absolute', top: 0, left: 0,
                                    width: '100%', height: '100%', pointerEvents: 'none'
                                }}
                            />
                            <canvas ref={scanCanvas} style={{ display: 'none' }} />
                        </Box>

                        {/* Status strip */}
                        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, bgcolor: BG_SUBTLE, borderTop: `1px solid ${BORDER_COLOR}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{
                                    fontWeight: 500,
                                    color: detectedCorners ? 'success.main' : TEXT_SECONDARY,
                                    transition: 'color 0.3s'
                                }}>
                                    {detectedCorners ? 'Receipt detected — hold steady…' : 'Point camera at a receipt'}
                                </Typography>
                                {stableProgress > 0 && (
                                    <Fade in>
                                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                            {Math.round(stableProgress)}%
                                        </Typography>
                                    </Fade>
                                )}
                            </Box>
                            {/* Progress bar — only visible while a receipt is detected */}
                            <LinearProgress
                                variant="determinate"
                                value={stableProgress}
                                sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    bgcolor: BORDER_COLOR,
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: stableProgress > 60 ? 'success.main' : 'primary.main',
                                        borderRadius: 2,
                                        transition: 'background-color 0.4s',
                                    },
                                    opacity: stableProgress > 0 ? 1 : 0,
                                    transition: 'opacity 0.3s',
                                }}
                            />
                            <Typography variant="caption" sx={{ color: TEXT_SECONDARY, display: 'block', mt: 0.75 }}>
                                Auto-captures when focused
                            </Typography>
                        </Box>
                    </>
                )}

                {(phase === ScannerPhase.PREVIEW || phase === ScannerPhase.UPLOADING) && previewUrl && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 2, bgcolor: BG_SUBTLE }}>
                        {/* Cropped receipt */}
                        <Box sx={{
                            position: 'relative',
                            width: '100%',
                            height: '52vh',
                            border: `1px solid ${BORDER_COLOR}`,
                            bgcolor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0px 1px 3px rgba(15,23,42,0.08)',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            <img
                                src={previewUrl}
                                alt="Captured receipt"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                            />
                            <IconButton
                                onClick={() => setIsMagnifiedOpen(true)}
                                sx={{
                                    position: 'absolute',
                                    bottom: 12,
                                    right: 12,
                                    bgcolor: 'rgba(15, 23, 42, 0.75)',
                                    color: '#fff',
                                    '&:hover': {
                                        bgcolor: 'rgba(15, 23, 42, 0.9)'
                                    },
                                    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
                                }}
                            >
                                <ZoomInIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                {t('Receipt captured!')}
                            </Typography>
                        </Box>

                        {uploadError && <Alert severity="error" sx={{ width: '100%' }}>{uploadError}</Alert>}
                    </Box>
                )}
            </DialogContent>

            {/* ── Actions ── */}
            <DialogActions sx={{
                borderTop: `1px solid ${BORDER_COLOR}`,
                px: 2.5, py: 2, gap: 1,
                bgcolor: '#fff',
            }}>
                {(phase === ScannerPhase.CAMERA || phase === ScannerPhase.FREEZING) && (
                    <Button onClick={handleClose} variant="contained" color="secondary">
                        {t('Cancel')}
                    </Button>
                )}
                {(phase === ScannerPhase.PREVIEW || phase === ScannerPhase.UPLOADING) && (
                    <>
                        <Button
                            onClick={handleRetake}
                            variant="contained"
                            color="secondary"
                            startIcon={<ReplayIcon />}
                            disabled={phase === ScannerPhase.UPLOADING}
                        >
                            {t('Retake')}
                        </Button>
                        <Button
                            onClick={handleUsePhoto}
                            variant="contained"
                            disabled={phase === ScannerPhase.UPLOADING}
                            startIcon={phase === ScannerPhase.UPLOADING
                                ? <CircularProgress size={16} color="inherit" />
                                : null}
                            sx={{ ml: 'auto' }}
                        >
                            {phase === ScannerPhase.UPLOADING ? t('Analyzing…') : t('Use this photo')}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>

        {/* ── Magnified view popup ── */}
        <Dialog
            open={isMagnifiedOpen}
            onClose={() => setIsMagnifiedOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Receipt Zoom')}</Typography>
                <IconButton onClick={() => setIsMagnifiedOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, bgcolor: BG_SUBTLE }}>
                <Box sx={{ width: '100%', maxHeight: '70vh', overflow: 'auto', display: 'flex', justifyContent: 'center', p: 3 }}>
                    <Box sx={{ minWidth: 'min-content' }}>
                        <img
                            src={previewUrl}
                            alt="Zoomed receipt"
                            style={{ display: 'block', boxShadow: '0px 4px 12px rgba(15,23,42,0.15)', borderRadius: '4px' }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                <Button onClick={() => setIsMagnifiedOpen(false)} variant="contained">
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
